import { Tracing } from "@tento-core/tracing";
import { DomFlags } from "@tento-ui/dom-flags";
import { FRAME_BUDGET, FramePace } from "@tento-ui/frame-budget";
import type { Option } from "@tento-lona/sheets";
import type { CalendarSheetColumn } from "./calendar-sheet-column.ts";

/**
 * How far calendar content trails the skeleton while the timeline moves.
 *
 * The skeleton (header, grid, cell background) paints in the frame a day
 * column becomes visible; its events arrive when the strip bind that covers
 * the day finishes (reveal-when-complete). This meter counts, per viewport
 * report while the visible days move (one per frame of motion), the visible
 * days that no finished strip bind shows yet, and times each such day from
 * the first report that saw it unbound to the bind that shows it.
 *
 * - Strips report through `CalendarSheetColumn.ContentLag`: `shown` when a
 *   bind finished (or reused a finished read) with the days it covers,
 *   `hidden` when a bind begins, a load supersedes it, the strip recycles or
 *   its column parks in the scroll pool (a parked column shows nothing).
 *   A day counts as bound while any strip shows it.
 * - A day that was already bound at the first report that saw it lags 0 and
 *   is counted in `boundAtReveal`; a day that scrolls out unbound is counted
 *   in `leftUnbound`, without a lag time.
 * - Days the visible range passed over between two reports (a jump of more
 *   than one viewport: fewer reports than the range moved by widths) are
 *   revealed too, and left unbound unless a strip already shows them; they
 *   are also counted in `jumpedDays`. So the same gesture counts the same
 *   days whatever the report rate. A move of more than
 *   {@link ContentLagMeter.JUMP_MAX_LENGTHS} visible lengths is a navigation,
 *   not motion: nothing between is counted.
 * - One summary per motion episode: once neither a viewport report nor a
 *   reveal came for {@link ContentLagMeter.QUIET_MS}, the meter emits
 *   `scroll/content-lag` (Tracing) and starts over. Episodes with no motion
 *   report emit nothing.
 *
 * Cost: a report walks the visible days once (map lookups, no allocation per
 * day); a reveal walks its strip's days. On in every arm, so the A/B windows
 * compare like with like.
 *
 * Bind pace (`calendar-bind-pace`, off by default): with a pace
 * source the meter also keeps each report (time, visible days, lagging days,
 * days revealed by it) and, with the episode's summary, emits
 * `scroll/bind-pace`: those reports beside the frame budget's per-frame
 * records over the same span (what each budgeted queue wanted, ran and
 * deferred; columns the engine reconciled), so a fling's unbound days can be
 * put against what the binders were doing frame by frame.
 *
 * Day states (stage 0 of the progressive columns plan;
 * `calendar-content-states`, off by default): with a `states`
 * sink the meter also classifies every visible day at every report
 * by what is on screen for it: `chips` (a revealed strip shows it), `rough`
 * (its rough boxes show, `calendar-rough-layer`), `blank` (its column's data
 * is installed, `loaded`, but neither shows) or `unloaded` (its data has not
 * arrived). What is on screen differs from `shown`: a same-date rebind keeps
 * the revealed chips painted, so `painted` days clear only when the strip
 * shows nothing (`unloaded`: a new date, recycle or park). Per episode it
 * emits `scroll/content-states`: the day-report counts, the share of
 * data-installed day reports showing neither boxes nor chips
 * (`blankOfEventsPct`), the time from the first report that saw a day
 * showing nothing to the first rough boxes or chips for it, and flash days
 * (a day that showed boxes or chips at one report and nothing at the next).
 * Without a sink none of this runs: no day is classified and no owner's days
 * are counted, so the default path pays only for the content lag.
 */
export class ContentLagMeter implements CalendarSheetColumn.ContentLag {
  /** Quiet time after the last report or reveal that ends an episode. */
  static readonly QUIET_MS = 400;
  /** The longest move between two reports, in visible lengths, whose passed-over days still count as revealed. */
  static readonly JUMP_MAX_LENGTHS = 4;

  /** The days each strip's finished bind shows. */
  readonly #daysOf = new Map<object, readonly number[]>();
  /** How many strips show each day. */
  readonly #shownCount = new Map<number, number>();
  /** Visible days not bound yet, by when a report first saw them so. */
  readonly #since = new Map<number, number>();
  #visible: Option<ContentLagMeter.Days> = null;
  #episode = ContentLagMeter.emptyEpisode();
  #cancelQuiet: Option<() => void> = null;
  /** This episode's reports for the bind-pace trace: [at, start, end, lagging, revealed]; null without a pace source. */
  #reports: Option<number[][]> = null;
  /** Days whose column data is installed (`loaded`, `shown`), by owner. */
  readonly #loaded = new ContentLagMeter.DayCounts();
  /** Days a revealed strip has painted, until it shows nothing (`unloaded`). */
  readonly #painted = new ContentLagMeter.DayCounts();
  /** Days whose rough boxes show (`rough`), until the strip reveals or shows nothing. */
  readonly #rough = new ContentLagMeter.DayCounts();
  /** Each visible day's state at the last report (day states). */
  #lastState = new Map<number, ContentLagMeter.DayState>();
  /** Visible days showing nothing, by when a report first saw them so (day states). */
  readonly #contentSince = new Map<number, number>();
  #states = ContentLagMeter.emptyStates();
  /** Whether day states are kept (the clock has a `states` sink). */
  readonly #keepsStates: boolean;

  constructor(readonly clock: ContentLagMeter.Clock) {
    if (clock.pace != null) this.#reports = [];
    this.#keepsStates = clock.states != null;
  }

  /** Whether the bind-pace trace is on (`calendar-bind-pace`, off by default); read when a meter is made. */
  static bindPaceEnabled(): boolean {
    return DomFlags.read("calendar-bind-pace", false);
  }

  /**
   * Whether the day-states record is on (`calendar-content-states`, off by
   * default; the perf windows arm it); read when a meter is made.
   */
  static contentStatesEnabled(): boolean {
    return DomFlags.read("calendar-content-states", false);
  }

  /** A meter on the page clock, emitting through Tracing. */
  static forDocument(): ContentLagMeter {
    const pace = ContentLagMeter.bindPaceEnabled();
    const states = ContentLagMeter.contentStatesEnabled();
    if (pace) FRAME_BUDGET.recordPace(true);
    return new ContentLagMeter({
      now: () => performance.now(),
      after: (ms, task) => {
        const handle = setTimeout(task, ms);
        return () => clearTimeout(handle);
      },
      emit: (summary) => Tracing.log("scroll/content-lag", summary),
      ...(states ? { states: (record: ContentLagMeter.States) => Tracing.log("scroll/content-states", record) } : {}),
      ...(pace ? { pace: {
        frames: (since: number) => FRAME_BUDGET.paceSince(since).map(FramePace.row),
        emit: (trace: ContentLagMeter.PaceTrace) => Tracing.log("scroll/bind-pace", trace),
      } } : {}),
    });
  }

  static emptyEpisode(): ContentLagMeter.Episode {
    return {
      frames: 0, framesWithLag: 0, laggingDaySum: 0, maxLaggingDays: 0, visibleDaySum: 0,
      revealedDays: 0, boundAtReveal: 0, leftUnbound: 0, jumpedDays: 0, reusedShows: 0, lags: [],
      firstStart: null, lastStart: null,
    };
  }

  static emptyStates(): ContentLagMeter.StateEpisode {
    return { frames: 0, chips: 0, rough: 0, blank: 0, unloaded: 0, flashDays: 0, contentLags: [] };
  }

  /** `owner`'s column data for `days` is installed: its strip bind began (day states). */
  loaded(owner: object, days: readonly number[]): void {
    if (!this.#keepsStates) return;
    this.#loaded.set(owner, days);
  }

  /** `owner` paints nothing for its days now: a new date's load, a recycle or a park (day states). */
  unloaded(owner: object): void {
    if (!this.#keepsStates) return;
    this.#loaded.delete(owner);
    this.#painted.delete(owner);
    this.#rough.delete(owner);
  }

  /** `owner`'s rough boxes show for `days` until its reveal or until it shows nothing (`calendar-rough-layer`). */
  rough(owner: object, days: readonly number[]): void {
    if (!this.#keepsStates) return;
    this.#rough.set(owner, days);
    this.#content(days, this.clock.now());
  }

  /** The first rough boxes or chips for `days`: each day that showed nothing gets its time to content. */
  #content(days: readonly number[], now: number): void {
    for (const day of days) {
      const since = this.#contentSince.get(day);
      if (since == null) continue;
      this.#contentSince.delete(day);
      this.#states.contentLags.push(now - since);
    }
  }

  /** `owner`'s finished bind shows `days` (days since the epoch); `"reuse"`: a same-read reuse, counted in `reusedShows`. */
  shown(owner: object, days: readonly number[], via: "bind" | "reuse"): void {
    this.hidden(owner);
    if (via === "reuse") this.#episode.reusedShows++;
    this.#daysOf.set(owner, days);
    const now = this.clock.now();
    if (this.#keepsStates) {
      this.#loaded.set(owner, days);
      this.#painted.set(owner, days);
      this.#rough.delete(owner);
      this.#content(days, now);
    }
    for (const day of days) {
      this.#shownCount.set(day, (this.#shownCount.get(day) ?? 0) + 1);
      const since = this.#since.get(day);
      if (since == null) continue;
      this.#since.delete(day);
      this.#episode.lags.push(now - since);
    }
    this.#touch();
  }

  /** `owner` shows no finished bind. */
  hidden(owner: object): void {
    const days = this.#daysOf.get(owner);
    if (days == null) return;
    this.#daysOf.delete(owner);
    for (const day of days) {
      const count = (this.#shownCount.get(day) ?? 0) - 1;
      if (count > 0) this.#shownCount.set(day, count);
      else this.#shownCount.delete(day);
    }
  }

  /** The visible days `[start, end)` now; a report with an unchanged range is not motion. */
  noteVisibleDays(days: ContentLagMeter.Days): void {
    const previous = this.#visible;
    this.#visible = days;
    if (previous != null && previous.start === days.start && previous.end === days.end) return;
    const now = this.clock.now();
    const episode = this.#episode;
    let lagging = 0;
    let revealed = 0;
    // Days passed over since the last report: revealed, and left unbound unless shown.
    const gap = previous == null ? null
      : days.start > previous.end ? { start: previous.end, end: days.start }
      : days.end < previous.start ? { start: days.end, end: previous.start }
      : null;
    const width = Math.max(1, days.end - days.start);
    if (gap != null && gap.end - gap.start <= ContentLagMeter.JUMP_MAX_LENGTHS * width) {
      for (let day = gap.start; day < gap.end; day++) {
        revealed++;
        episode.revealedDays++;
        episode.jumpedDays++;
        if (this.#shownCount.has(day)) episode.boundAtReveal++;
        else episode.leftUnbound++;
      }
    }
    for (let day = days.start; day < days.end; day++) {
      const bound = this.#shownCount.has(day);
      const fresh = previous == null || day < previous.start || day >= previous.end;
      if (fresh) {
        revealed++;
        episode.revealedDays++;
        if (bound) episode.boundAtReveal++;
      }
      if (bound) continue;
      lagging++;
      if (!this.#since.has(day)) this.#since.set(day, now);
    }
    for (const day of this.#since.keys()) {
      if (day >= days.start && day < days.end) continue;
      this.#since.delete(day);
      episode.leftUnbound++;
    }
    episode.firstStart ??= days.start;
    episode.lastStart = days.start;
    episode.frames++;
    episode.visibleDaySum += Math.max(0, days.end - days.start);
    episode.laggingDaySum += lagging;
    if (lagging > 0) episode.framesWithLag++;
    if (lagging > episode.maxLaggingDays) episode.maxLaggingDays = lagging;
    this.#reports?.push([now, days.start, days.end, lagging, revealed]);
    if (this.#keepsStates) this.#noteStates(days, now);
    this.#touch();
  }

  /** What each visible day shows at this report, flash days, and when a day began showing nothing. */
  #noteStates(days: ContentLagMeter.Days, now: number): void {
    const states = this.#states;
    const next = new Map<number, ContentLagMeter.DayState>();
    for (let day = days.start; day < days.end; day++) {
      const state: ContentLagMeter.DayState = this.#painted.has(day) ? "chips"
        : this.#rough.has(day) ? "rough"
        : this.#loaded.has(day) ? "blank"
        : "unloaded";
      states[state]++;
      const before = this.#lastState.get(day);
      const empty = state === "blank" || state === "unloaded";
      if (empty && (before === "chips" || before === "rough")) states.flashDays++;
      if (empty && !this.#contentSince.has(day)) this.#contentSince.set(day, now);
      next.set(day, state);
    }
    for (const day of this.#contentSince.keys()) {
      if (day < days.start || day >= days.end) this.#contentSince.delete(day);
    }
    this.#lastState = next;
    states.frames++;
  }

  /** End the episode now: emit its summary if it saw motion, and start over. */
  flush(): Option<ContentLagMeter.Summary> {
    this.#cancelQuiet?.();
    this.#cancelQuiet = null;
    const episode = this.#episode;
    this.#episode = ContentLagMeter.emptyEpisode();
    const states = this.#states;
    this.#states = ContentLagMeter.emptyStates();
    this.#lastState = new Map();
    this.#contentSince.clear();
    if (states.frames > 0) this.clock.states?.(ContentLagMeter.summarizeStates(states, episode));
    const reports = this.#reports;
    if (reports != null) this.#reports = [];
    if (episode.frames === 0) return null;
    const summary = ContentLagMeter.summarize(episode, this.#since.size);
    this.clock.emit(summary);
    const pace = this.clock.pace;
    const first = reports?.[0]?.[0];
    if (pace != null && reports != null && first != null) {
      // Frames from one vsync before the first report: the frame whose scroll revealed it.
      pace.emit(ContentLagMeter.paceTrace(summary, reports, pace.frames(first - 17)));
    }
    return summary;
  }

  /** The `scroll/bind-pace` trace of one episode: times in ms after its first report, rounded to 0.1 ms. */
  static paceTrace(
    summary: ContentLagMeter.Summary,
    reports: readonly (readonly number[])[],
    frames: readonly (readonly number[])[],
  ): ContentLagMeter.PaceTrace {
    const t0 = reports[0]?.[0] ?? 0;
    const round = (ms: number) => Math.round(ms * 10) / 10;
    return {
      revealedDays: summary.revealedDays,
      leftUnbound: summary.leftUnbound,
      jumpedDays: summary.jumpedDays,
      reports: reports.map(([at = 0, ...rest]) => [round(at - t0), ...rest]),
      frames: frames.map(([openedAt = 0, ...rest]) => [round(openedAt - t0), ...rest]),
    };
  }

  /** The `scroll/content-states` record of one episode (rounded to 0.1). */
  static summarizeStates(states: ContentLagMeter.StateEpisode, episode: ContentLagMeter.Episode): ContentLagMeter.States {
    const lags = [...states.contentLags].sort((a, b) => a - b);
    const at = (q: number) => lags.length === 0 ? 0 : lags[Math.min(lags.length - 1, Math.ceil(q * lags.length) - 1)] ?? 0;
    const round = (value: number) => Math.round(value * 10) / 10;
    const withData = states.chips + states.rough + states.blank;
    return {
      frames: states.frames,
      chips: states.chips,
      rough: states.rough,
      blank: states.blank,
      unloaded: states.unloaded,
      blankOfEventsPct: withData === 0 ? 0 : round(100 * states.blank / withData),
      contentDays: lags.length,
      contentLagP50Ms: round(at(0.5)),
      contentLagP95Ms: round(at(0.95)),
      flashDays: states.flashDays,
      firstStart: episode.firstStart,
      lastStart: episode.lastStart,
    };
  }

  /** The summary of `episode`, with `unboundAtEnd` visible days still waiting. */
  static summarize(episode: ContentLagMeter.Episode, unboundAtEnd: number): ContentLagMeter.Summary {
    const lags = [...episode.lags].sort((a, b) => a - b);
    const at = (q: number) => lags.length === 0 ? 0 : lags[Math.min(lags.length - 1, Math.ceil(q * lags.length) - 1)] ?? 0;
    const round = (ms: number) => Math.round(ms * 10) / 10;
    return {
      frames: episode.frames,
      framesWithLag: episode.framesWithLag,
      meanLaggingDays: round(episode.laggingDaySum / episode.frames),
      maxLaggingDays: episode.maxLaggingDays,
      meanVisibleDays: round(episode.visibleDaySum / episode.frames),
      revealedDays: episode.revealedDays,
      boundAtReveal: episode.boundAtReveal,
      lateDays: lags.length,
      lagP50Ms: round(at(0.5)),
      lagP95Ms: round(at(0.95)),
      lagMaxMs: round(lags[lags.length - 1] ?? 0),
      leftUnbound: episode.leftUnbound,
      jumpedDays: episode.jumpedDays,
      unboundAtEnd,
      reusedShows: episode.reusedShows,
      firstStart: episode.firstStart,
      lastStart: episode.lastStart,
    };
  }

  #touch(): void {
    this.#cancelQuiet?.();
    this.#cancelQuiet = this.clock.after(ContentLagMeter.QUIET_MS, () => {
      this.#cancelQuiet = null;
      this.flush();
    });
  }
}

/** Types that go with `ContentLagMeter`. */
export namespace ContentLagMeter {
  /** Days since the epoch, end exclusive. */
  export type Days = { readonly start: number; readonly end: number };

  /** The meter's clock, timer and sink (the page's in production, a fake in tests). */
  export type Clock = {
    readonly now: () => number;
    /** Run `task` after `ms`; returns a cancel. */
    readonly after: (ms: number, task: () => void) => () => void;
    readonly emit: (summary: Summary) => void;
    /** The day-states record's sink (`scroll/content-states`); absent = day states are not kept. */
    readonly states?: (states: States) => void;
    /** The bind-pace trace's frame records and sink; absent = no trace. */
    readonly pace?: {
      /** The frame budget's pace rows (`FramePace.row`) of frames opened at or after `since`. */
      readonly frames: (since: number) => readonly (readonly number[])[];
      readonly emit: (trace: PaceTrace) => void;
    };
  };

  /**
   * One episode's `scroll/bind-pace` trace. `reports`: [ms, start day, end day,
   * lagging days, days revealed]. `frames`: [ms opened, content deadline (-1 =
   * none), trim, columns reconciled, binds wanted, ran, deferred, strip units
   * wanted, ran, deferred, fixed units wanted, ran, deferred, refusals, most
   * deadline left at a refusal].
   */
  export type PaceTrace = {
    readonly revealedDays: number;
    readonly leftUnbound: number;
    readonly jumpedDays: number;
    readonly reports: readonly (readonly number[])[];
    readonly frames: readonly (readonly number[])[];
  };

  /** What one episode has counted so far. */
  export type Episode = {
    frames: number;
    framesWithLag: number;
    laggingDaySum: number;
    maxLaggingDays: number;
    visibleDaySum: number;
    revealedDays: number;
    boundAtReveal: number;
    leftUnbound: number;
    jumpedDays: number;
    reusedShows: number;
    readonly lags: number[];
    /** The visible start day at the episode's first and latest report. */
    firstStart: Option<number>;
    lastStart: Option<number>;
  };

  /** What a visible day shows at a report (day states). */
  export type DayState = "chips" | "rough" | "blank" | "unloaded";

  /** What one episode has counted of day states so far. */
  export type StateEpisode = {
    frames: number;
    chips: number;
    rough: number;
    blank: number;
    unloaded: number;
    flashDays: number;
    readonly contentLags: number[];
  };

  /** One motion episode's day states, as `scroll/content-states` logs it. */
  export type States = {
    /** Viewport reports with a moved range. */
    readonly frames: number;
    /** Visible-day reports by state: chips, rough boxes, data installed but nothing shown, no data yet. */
    readonly chips: number;
    readonly rough: number;
    readonly blank: number;
    readonly unloaded: number;
    /** `blank` as a share of the reports whose day had data (chips + rough + blank), percent. */
    readonly blankOfEventsPct: number;
    /** Days that showed nothing at a report and later rough boxes or chips; their time to that content. */
    readonly contentDays: number;
    readonly contentLagP50Ms: number;
    readonly contentLagP95Ms: number;
    /** Day reports that showed nothing right after a report that showed rough boxes or chips for the day. */
    readonly flashDays: number;
    readonly firstStart: Option<number>;
    readonly lastStart: Option<number>;
  };

  /** How many owners show each day (an owner's days replace its earlier ones). */
  export class DayCounts {
    readonly #of = new Map<object, readonly number[]>();
    readonly #count = new Map<number, number>();

    set(owner: object, days: readonly number[]): void {
      this.delete(owner);
      this.#of.set(owner, days);
      for (const day of days) this.#count.set(day, (this.#count.get(day) ?? 0) + 1);
    }

    delete(owner: object): void {
      const days = this.#of.get(owner);
      if (days == null) return;
      this.#of.delete(owner);
      for (const day of days) {
        const count = (this.#count.get(day) ?? 0) - 1;
        if (count > 0) this.#count.set(day, count);
        else this.#count.delete(day);
      }
    }

    has(day: number): boolean {
      return this.#count.has(day);
    }
  }

  /** One motion episode, as `scroll/content-lag` logs it. */
  export type Summary = {
    /** Viewport reports with a moved range (one per frame of motion). */
    readonly frames: number;
    /** Of those, reports that saw at least one visible day unbound. */
    readonly framesWithLag: number;
    readonly meanLaggingDays: number;
    readonly maxLaggingDays: number;
    readonly meanVisibleDays: number;
    /** Days that entered the visible range. */
    readonly revealedDays: number;
    /** Of those, days already bound at the first report that saw them. */
    readonly boundAtReveal: number;
    /** Days seen visible and unbound that a bind then showed (each has a lag). */
    readonly lateDays: number;
    /** From the first report that saw a day visible and unbound to the bind that showed it. */
    readonly lagP50Ms: number;
    readonly lagP95Ms: number;
    readonly lagMaxMs: number;
    /** Days that scrolled out while still unbound (passed-over days included). */
    readonly leftUnbound: number;
    /** Of the revealed days, those the range passed over between two reports. */
    readonly jumpedDays: number;
    /** Visible days still unbound when the episode ended. */
    readonly unboundAtEnd: number;
    /** Strips shown by reusing a finished read (no paint), e.g. a column reattached at its own date. */
    readonly reusedShows: number;
    /** The visible start day (days since the epoch) at the first and the last report: which stretch of dates the episode covered. */
    readonly firstStart: Option<number>;
    readonly lastStart: Option<number>;
  };
}
